import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Page,
  Card,
  FormLayout,
  TextField,
  BlockStack,
  Banner,
  Text,
  InlineStack,
  Badge,
  Button,
  Checkbox,
  Divider,
  Box,
  Spinner,
} from '@shopify/polaris';
import { useApi } from '../providers/ApiProvider';
import {
  ContextualSaveBar,
  SavingOverlay,
  isShopifyEmbedded,
  showAppToast,
  useUnsavedChanges,
} from '../components/ContextualSaveBar';
import { openShopifyAdminUrl } from '../lib/shopifyNav';
import { themeEmbedDeepLink } from '../lib/shopContext';
import { t } from '../i18n';
import { AdminLanguageCard } from '../components/AdminLanguageCard';

interface StoreSetupStatus {
  shopDomain: string;
  appEmbed: {
    active: boolean;
    statusLabel: 'Active' | 'Inactive';
    message: string;
    deepLink: string;
    blockName?: string;
    themeName?: string | null;
    source?: string;
  };
}

interface StorefrontSettingsForm {
  popupReappearMinutes: string;
  popupReappearSeconds: string;
  /** After customer removes gift: wait then retry auto-add */
  preventGiftReadd: boolean;
  giftReaddMinutes: string;
  giftReaddSeconds: string;
  /** After gift was added once: never auto re-add again this browser session */
  preventGiftReaddSession: boolean;
}

/** Default 3 seconds for popup reappear and cart gift re-add */
const DEFAULTS: StorefrontSettingsForm = {
  popupReappearMinutes: '0',
  popupReappearSeconds: '3',
  preventGiftReadd: true,
  giftReaddMinutes: '0',
  giftReaddSeconds: '3',
  preventGiftReaddSession: false,
};

function clampInt(raw: string, max = 9999): number {
  const n = parseInt(String(raw).replace(/\D/g, '') || '0', 10);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.min(n, max);
}

function normalizeForm(f: StorefrontSettingsForm): StorefrontSettingsForm {
  const sec = clampInt(f.popupReappearSeconds, 59);
  const min = clampInt(f.popupReappearMinutes);
  const gSec = clampInt(f.giftReaddSeconds, 59);
  const gMin = clampInt(f.giftReaddMinutes);
  // Mutual exclusivity: session lock wins if both were somehow set
  let preventGiftReadd = !!f.preventGiftReadd;
  let preventGiftReaddSession = !!f.preventGiftReaddSession;
  if (preventGiftReadd && preventGiftReaddSession) {
    preventGiftReaddSession = true;
    preventGiftReadd = false;
  }
  return {
    popupReappearMinutes: String(min),
    popupReappearSeconds: String(sec),
    preventGiftReadd,
    giftReaddMinutes: String(gMin),
    giftReaddSeconds: String(gSec),
    preventGiftReaddSession,
  };
}

export function SettingsPage() {
  const api = useApi();
  const embedded = isShopifyEmbedded();

  const [setup, setSetup] = useState<StoreSetupStatus | null>(null);
  const [setupLoading, setSetupLoading] = useState(true);
  const [embedBusy, setEmbedBusy] = useState(false);

  const [form, setForm] = useState<StorefrontSettingsForm>(DEFAULTS);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const { dirty, markClean, getBaseline, markCleanAfterDiscard } =
    useUnsavedChanges(form);

  const loadSetup = useCallback(async () => {
    try {
      const s = await api.get<StoreSetupStatus>('/stores/setup');
      setSetup(s);
      return s;
    } catch {
      setSetup(null);
      return null;
    } finally {
      setSetupLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void loadSetup();
    const t = window.setInterval(() => void loadSetup(), 15000);
    return () => window.clearInterval(t);
  }, [loadSetup]);

  useEffect(() => {
    api
      .get<{
        settings?: Record<string, unknown>;
      }>('/stores/me')
      .then((s) => {
        const st = (s.settings || {}) as Record<string, unknown>;
        const hasPopup =
          st.popupReappearMinutes != null || st.popupReappearSeconds != null;
        const hasGift =
          st.giftReaddMinutes != null || st.giftReaddSeconds != null;
        const next = normalizeForm({
          popupReappearMinutes: String(
            hasPopup ? (st.popupReappearMinutes ?? 0) : DEFAULTS.popupReappearMinutes,
          ),
          popupReappearSeconds: String(
            hasPopup ? (st.popupReappearSeconds ?? 0) : DEFAULTS.popupReappearSeconds,
          ),
          preventGiftReadd:
            st.preventGiftReadd === undefined
              ? DEFAULTS.preventGiftReadd
              : st.preventGiftReadd === true,
          giftReaddMinutes: String(
            hasGift ? (st.giftReaddMinutes ?? 0) : DEFAULTS.giftReaddMinutes,
          ),
          giftReaddSeconds: String(
            hasGift ? (st.giftReaddSeconds ?? 0) : DEFAULTS.giftReaddSeconds,
          ),
          preventGiftReaddSession:
            st.preventGiftReaddSession === undefined
              ? DEFAULTS.preventGiftReaddSession
              : st.preventGiftReaddSession === true,
        });
        setForm(next);
        markClean(next);
        setLoaded(true);
      })
      .catch(() => {
        markClean(DEFAULTS);
        setLoaded(true);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once
  }, [api]);

  const active = setup?.appEmbed?.active === true;
  const themeName = setup?.appEmbed?.themeName || 'Current theme';
  const deepLink =
    themeEmbedDeepLink(setup?.appEmbed?.deepLink, setup?.shopDomain) || '';

  const popupHelp = useMemo(() => {
    const m = clampInt(form.popupReappearMinutes);
    const s = clampInt(form.popupReappearSeconds, 59);
    if (m === 0 && s === 0) {
      return 'Popup can show again immediately after the customer closes it.';
    }
    return `After closing, the popup waits ${m}m ${s}s before showing again.`;
  }, [form.popupReappearMinutes, form.popupReappearSeconds]);

  const cartHelp = useMemo(() => {
    if (form.preventGiftReaddSession) {
      return 'If the customer removes a free gift, Freebi will not auto re-add it again.';
    }
    if (form.preventGiftReadd) {
      const m = clampInt(form.giftReaddMinutes);
      const s = clampInt(form.giftReaddSeconds, 59);
      if (m === 0 && s === 0) {
        return 'After removal, Freebi will not auto re-add (set a delay above 0 to retry).';
      }
      return `After removal, Freebi waits ${m}m ${s}s before auto re-adding.`;
    }
    return 'Gifts are re-added as soon as the campaign is still eligible. Choose one option below, or leave both off.';
  }, [
    form.preventGiftReadd,
    form.preventGiftReaddSession,
    form.giftReaddMinutes,
    form.giftReaddSeconds,
  ]);

  function patchForm(partial: Partial<StorefrontSettingsForm>) {
    setForm((prev) => ({ ...prev, ...partial }));
  }

  /** Only one cart re-add mode can be active at a time */
  function setRetryReaddAfterRemoval(checked: boolean) {
    if (checked) {
      patchForm({
        preventGiftReadd: true,
        preventGiftReaddSession: false,
      });
    } else {
      patchForm({ preventGiftReadd: false });
    }
  }

  function setDoNotReaddAfterRemoval(checked: boolean) {
    if (checked) {
      patchForm({
        preventGiftReaddSession: true,
        preventGiftReadd: false,
      });
    } else {
      patchForm({ preventGiftReaddSession: false });
    }
  }

  async function save() {
    const normalized = normalizeForm(form);
    setForm(normalized);
    setSaving(true);
    try {
      await api.patch('/stores/me', {
        settings: {
          popupReappearMinutes: clampInt(normalized.popupReappearMinutes),
          popupReappearSeconds: clampInt(normalized.popupReappearSeconds, 59),
          preventGiftReadd: normalized.preventGiftReadd,
          giftReaddMinutes: clampInt(normalized.giftReaddMinutes),
          giftReaddSeconds: clampInt(normalized.giftReaddSeconds, 59),
          preventGiftReaddSession: normalized.preventGiftReaddSession,
        },
      });
      markClean(normalized);
      setSaved(true);
      showAppToast(t("Settings saved"));
    } catch (e) {
      showAppToast(e instanceof Error ? e.message : 'Save failed', {
        isError: true,
      });
    } finally {
      setSaving(false);
    }
  }

  function discard() {
    const base = getBaseline() ?? DEFAULTS;
    setForm(base);
    markCleanAfterDiscard(base);
  }

  function openEmbedEditor() {
    if (!deepLink) {
      showAppToast(
        t("Theme editor link unavailable. Re-open the app from Shopify admin."),
        { isError: true },
      );
      return;
    }
    setEmbedBusy(true);
    openShopifyAdminUrl(deepLink, { newTab: true });
    [3, 8, 15].forEach((sec) => {
      window.setTimeout(() => void loadSetup(), sec * 1000);
    });
    window.setTimeout(() => setEmbedBusy(false), 800);
  }

  return (
    <Page
      title={t("Settings")}
      primaryAction={
        !embedded && loaded
          ? {
              content: t("Save"),
              loading: saving,
              disabled: !dirty,
              onAction: () => void save(),
            }
          : undefined
      }
    >
      <ContextualSaveBar
        id="freebi-settings-save"
        open={embedded && dirty}
        saving={saving}
        onSave={save}
        onDiscard={discard}
      />
      <SavingOverlay open={saving} label={t("Saving settings…")} />

      <BlockStack gap="500">
        {saved && (
          <Banner tone="success" onDismiss={() => setSaved(false)}>
            {t("Settings saved")}
          </Banner>
        )}

        <AdminLanguageCard />

        {/* ── Theme embed ─────────────────────────────────────────── */}
        <Card>
          <BlockStack gap="400">
            <InlineStack align="space-between" blockAlign="center" wrap>
              <BlockStack gap="100">
                <Text as="h2" variant="headingMd">
                  {t("Theme app embed")}
                </Text>
                <Text as="p" tone="subdued" variant="bodySm">
                  {t("Freebi runs on your live theme. Turn the embed off in the theme\n                  editor to deactivate storefront gifts.")}
                </Text>
              </BlockStack>
              {setupLoading && !setup ? (
                <Spinner size="small" />
              ) : (
                <Badge tone={active ? 'success' : 'warning'}>
                  {active ? 'Active' : 'Inactive'}
                </Badge>
              )}
            </InlineStack>

            <Box
              padding="300"
              background="bg-surface-secondary"
              borderRadius="200"
            >
              <InlineStack align="space-between" blockAlign="center" wrap gap="300">
                <BlockStack gap="050">
                  <Text as="p" variant="bodySm" tone="subdued">
                    {t("Theme")}
                  </Text>
                  <Text as="p" variant="bodyMd" fontWeight="semibold">
                    {themeName}
                  </Text>
                </BlockStack>
                <BlockStack gap="050">
                  <Text as="p" variant="bodySm" tone="subdued">
                    {t("Status")}
                  </Text>
                  <Text as="p" variant="bodyMd" fontWeight="semibold">
                    {active ? 'Active' : 'Inactive'}
                  </Text>
                </BlockStack>
              </InlineStack>
            </Box>

            {setup?.appEmbed?.message ? (
              <Text as="p" tone="subdued" variant="bodySm">
                {setup.appEmbed.message}
              </Text>
            ) : null}

            <InlineStack gap="200" wrap>
              {active ? (
                <button
                  type="button"
                  disabled={embedBusy}
                  onClick={openEmbedEditor}
                  style={{
                    appearance: 'none',
                    border: 'none',
                    borderRadius: 8,
                    background: '#111111',
                    color: '#ffffff',
                    fontWeight: 600,
                    fontSize: 14,
                    lineHeight: '20px',
                    padding: '8px 16px',
                    cursor: embedBusy ? 'wait' : 'pointer',
                    opacity: embedBusy ? 0.7 : 1,
                  }}
                >
                  {embedBusy ? 'Opening…' : 'Deactivate'}
                </button>
              ) : (
                <Button
                  variant="primary"
                  loading={embedBusy}
                  onClick={openEmbedEditor}
                >
                  {t("Activate")}
                </Button>
              )}
              <Button onClick={() => void loadSetup()} loading={setupLoading}>
                {t("Refresh status")}
              </Button>
            </InlineStack>
          </BlockStack>
        </Card>

        {/* ── Popup ───────────────────────────────────────────────── */}
        <Card>
          <BlockStack gap="400">
            <BlockStack gap="100">
              <Text as="h2" variant="headingMd">
                {t("Popup")}
              </Text>
              <Text as="p" tone="subdued" variant="bodySm">
                {t("Control how soon the gift chooser popup can appear again after a\n                customer closes it.")}
              </Text>
            </BlockStack>

            <FormLayout>
              <FormLayout.Group condensed>
                <TextField
                  label={t("Minutes")}
                  type="number"
                  min={0}
                  autoComplete="off"
                  value={form.popupReappearMinutes}
                  onChange={(v) => patchForm({ popupReappearMinutes: v })}
                />
                <TextField
                  label={t("Seconds")}
                  type="number"
                  min={0}
                  max={59}
                  autoComplete="off"
                  value={form.popupReappearSeconds}
                  onChange={(v) => patchForm({ popupReappearSeconds: v })}
                />
              </FormLayout.Group>
            </FormLayout>
            <Text as="p" tone="subdued" variant="bodySm">
              {popupHelp}
            </Text>
          </BlockStack>
        </Card>

        {/* ── Cart ────────────────────────────────────────────────── */}
        <Card>
          <BlockStack gap="400">
            <BlockStack gap="100">
              <Text as="h2" variant="headingMd">
                {t("Cart")}
              </Text>
              <Text as="p" tone="subdued" variant="bodySm">
                {t("Control free gift auto-add when a customer removes a freebie.\n                First-time add still happens as soon as the campaign qualifies.")}
              </Text>
            </BlockStack>

            {/* Setting 1: timed retry after removal (mutually exclusive with #2) */}
            <Checkbox
              label={t("Retry re-adding after removal")}
              checked={form.preventGiftReadd}
              onChange={setRetryReaddAfterRemoval}
              helpText={t("Wait a set time, then auto-add the gift again if the customer removes it.")}
            />

            {form.preventGiftReadd && (
              <BlockStack gap="200">
                <Text as="p" variant="bodyMd" fontWeight="semibold">
                  {t("Retry after")}
                </Text>
                <FormLayout>
                  <FormLayout.Group condensed>
                    <TextField
                      label={t("Minutes")}
                      type="number"
                      min={0}
                      autoComplete="off"
                      value={form.giftReaddMinutes}
                      onChange={(v) => patchForm({ giftReaddMinutes: v })}
                    />
                    <TextField
                      label={t("Seconds")}
                      type="number"
                      min={0}
                      max={59}
                      autoComplete="off"
                      value={form.giftReaddSeconds}
                      onChange={(v) => patchForm({ giftReaddSeconds: v })}
                    />
                  </FormLayout.Group>
                </FormLayout>
              </BlockStack>
            )}

            <Divider />

            {/* Setting 2: never re-add after removal (mutually exclusive with #1) */}
            <Checkbox
              label={t("Do not re-add gift again after removal")}
              checked={form.preventGiftReaddSession}
              onChange={setDoNotReaddAfterRemoval}
              helpText={t("If the customer removes the free gift, Freebi will not auto-add it again this session.")}
            />

            <Text as="p" tone="subdued" variant="bodySm">
              {cartHelp}
            </Text>
          </BlockStack>
        </Card>

        {/* Bottom breathing room so last card isn’t flush against the iframe edge */}
        <Box paddingBlockEnd="800" />
      </BlockStack>
    </Page>
  );
}
